import React from 'react';

import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
  Button
} from '@chakra-ui/react';
import {
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  ListOrdered,
  List,
  Link as LinkIcon,
  MoreHorizontal,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Code,
  CheckSquare
} from 'lucide-react';
import { FORMAT_ELEMENT_COMMAND, LexicalEditor } from 'lexical';
import useModal from '../../../../hooks/useModal';
import { AutoEmbedDialog, TwitterEmbedConfig, YoutubeEmbedConfig } from '../../../AutoEmbedPlugin';
import { EditorStateHook } from '../../../../BaseEditor/hooks/useBaseEditorFormatState';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/extension';
import GiphySearch from '../../../../components/GiphyModal';
import { InsertMediaDialog } from '../../../MediaPlugin';
import { INSERT_POLL_COMMAND } from '../../../PollPlugin';

interface FormatSectionProps {
  activeEditor: LexicalEditor;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isCode: boolean;
  isLink: boolean;
  onBoldClick: () => void;
  onItalicClick: () => void;
  onUnderlineClick: () => void;
  onCodeClick: () => void;
  onLinkClick: () => void;
}

export const ToolbarButton = React.forwardRef<
  HTMLButtonElement,
  {
    isActive?: boolean;
    'aria-label': string;
    children: React.ReactNode;
    onClick?: () => void;
    className?: string;
  }
>(({ isActive, children, ...props }, ref) => (
  <button
    ref={ref}
    className={`
            p-1.5
            rounded-md
            text-base
            transition-all
            relative
            ${
              isActive
                ? 'text-green-600 bg-green-50 shadow-inner'
                : 'text-gray-600 bg-white shadow-[2px_2px_4px_rgba(0,0,0,0.03),-2px_-2px_4px_rgba(255,255,255,0.8)]'
            }
            hover:shadow-[inset_2px_2px_4px_rgba(0,0,0,0.03),inset_-2px_-2px_4px_rgba(255,255,255,0.8)]
            hover:text-green-600
        `}
    type="button"
    {...props}
  >
    {children}
  </button>
));

export const FormatSection: React.FC<FormatSectionProps> = ({
  isBold,
  isItalic,
  isUnderline,
  isCode,
  isLink,
  onBoldClick,
  onItalicClick,
  onUnderlineClick,
  onCodeClick,
  onLinkClick
}) => {
  return (
    <>
      <button
        onClick={onBoldClick}
        className={'toolbar-item !p-1 md:!p-2 md:spaced ' + (isBold ? 'active' : '')}
        title="Bold"
        type="button"
        aria-label="Format Bold"
      >
        <i className="format bold" />
      </button>
      <button
        onClick={onItalicClick}
        className={'toolbar-item !p-1 md:!p-2 md:spaced ' + (isItalic ? 'active' : '')}
        title="Italic"
        type="button"
        aria-label="Format Italic"
      >
        <i className="format italic" />
      </button>
      <button
        onClick={onUnderlineClick}
        className={'toolbar-item !p-1 md:!p-2 md:spaced ' + (isUnderline ? 'active' : '')}
        title="Underline"
        type="button"
        aria-label="Format Underline"
      >
        <i className="format underline" />
      </button>
      <button
        onClick={onCodeClick}
        className={'toolbar-item !p-1 md:!p-2 md:spaced ' + (isCode ? 'active' : '')}
        title="Code Block"
        type="button"
        aria-label="Insert code block"
      >
        <i className="format code" />
      </button>
      <button
        onClick={onLinkClick}
        className={'toolbar-item !p-1 md:!p-2 md:spaced ' + (isLink ? 'active' : '')}
        aria-label="Insert link"
        title="Insert link"
        type="button"
      >
        <i className="format link" />
      </button>
    </>
  );
};

export const TextStyleMenu = ({
  formatParagraph,
  formatHeading,
  formatList,
  formatQuote,
  activeEditor
}) => {
  return (
    <Menu>
      <Tooltip label="Text Style">
        <MenuButton as={ToolbarButton} aria-label="Text formatting">
          <Type size={18} />
        </MenuButton>
      </Tooltip>
      <MenuList style={{ zIndex: 9999999999 }} className="min-w-[180px]">
        <MenuItem onClick={formatParagraph}>Normal Text</MenuItem>
        <MenuItem onClick={() => formatHeading('h1')}>
          <Heading1 size={18} className="mr-2" />
          Heading 1
        </MenuItem>
        <MenuItem onClick={() => formatHeading('h2')}>
          <Heading2 size={18} className="mr-2" />
          Heading 2
        </MenuItem>
        <MenuItem onClick={() => formatHeading('h3')}>
          <Heading3 size={18} className="mr-2" />
          Heading 3
        </MenuItem>
        <MenuDivider />
        <MenuItem onClick={() => formatList('bullet')}>
          <List size={18} className="mr-2" />
          Bullet List
        </MenuItem>
        <MenuItem onClick={() => formatList('number')}>
          <ListOrdered size={18} className="mr-2" />
          Numbered List
        </MenuItem>
        <MenuItem onClick={() => formatList('check')}>
          <CheckSquare size={18} className="mr-2" />
          Check List
        </MenuItem>
        <MenuDivider />
        <MenuItem onClick={() => activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}>
          <AlignLeft size={18} className="mr-2" />
          Left Align
        </MenuItem>
        <MenuItem onClick={() => activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}>
          <AlignCenter size={18} className="mr-2" />
          Center
        </MenuItem>
        <MenuItem onClick={() => activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}>
          <AlignRight size={18} className="mr-2" />
          Right Align
        </MenuItem>
        <MenuItem onClick={() => activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}>
          <AlignJustify size={18} className="mr-2" />
          Justify
        </MenuItem>
        <MenuItem onClick={() => formatQuote()}>
          <Quote size={18} className="mr-2" />
          Quote
        </MenuItem>
      </MenuList>
      {/*</Portal>*/}
    </Menu>
  );
};

interface InsertMenuProps {
  editor: LexicalEditor;
  editorState: Pick<EditorStateHook, 'formatCode' | 'toggleLink' | 'isLink'>;
  setIsLinkEditMode: (isEdit: boolean) => void;
}

export const InsertMenu: React.FC<InsertMenuProps> = ({
  editor,
  editorState,
  setIsLinkEditMode
}) => {
  const [modal, showModal] = useModal();

  const handleEmbed = (embedConfig) => {
    showModal(`Embed ${embedConfig.contentName}`, (onClose) => (
      <AutoEmbedDialog embedConfig={embedConfig} onClose={onClose} />
    ));
  };

  const handleInsertPoll = () => {
    editor.dispatchCommand(INSERT_POLL_COMMAND, undefined);
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
  };

  const handleInsertGif = () => {
    showModal('', (onClose) => (
      <GiphySearch activeEditor={editor} onClose={onClose} isCommentEditor={true} />
    ));
  };

  const handleInsertMedia = () => {
    showModal('Insert Media', (onClose) => (
      <InsertMediaDialog
        activeEditor={editor}
        onClose={onClose}
        isHiddenContributorSection
        isCommentEditor
      />
    ));
  };

  return (
    <Menu>
      <MenuButton className="toolbar-item !p-1 md:!p-2" p={2} aria-label="Insert content">
        <MoreHorizontal size={18} />
      </MenuButton>
      <MenuList minW={'unset'} p={'0.625rem'} shadow={'md'}>
        <MenuItem
          _hover={{ bgColor: 'anti-flash-white' }}
          rounded={'0.5rem'}
          py={'8px'}
          px={'10px'}
          _focus={{ bgColor: 'anti-flash-white' }}
          className="menu-item"
          onClick={() => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)}
        >
          <i className="horizontal-rule icon" />
          <span className="text">Horizontal Rule</span>
        </MenuItem>
        <MenuItem
          _hover={{ bgColor: 'anti-flash-white' }}
          rounded={'0.5rem'}
          py={'8px'}
          px={'10px'}
          _focus={{ bgColor: 'anti-flash-white' }}
          className="menu-item"
          onClick={() => handleInsertMedia()}
        >
          <i className="image icon" />
          <span className="text">Media</span>
        </MenuItem>
        <MenuItem
          _hover={{ bgColor: 'anti-flash-white' }}
          rounded={'0.5rem'}
          py={'8px'}
          px={'10px'}
          _focus={{ bgColor: 'anti-flash-white' }}
          className="menu-item"
          onClick={() => handleInsertGif()}
        >
          <i className="gif icon" />
          <span className="text">GIF</span>
        </MenuItem>
        <MenuItem
          _hover={{ bgColor: 'anti-flash-white' }}
          rounded={'0.5rem'}
          py={'8px'}
          px={'10px'}
          _focus={{ bgColor: 'anti-flash-white' }}
          className="menu-item"
          onClick={() => handleInsertPoll()}
        >
          <i className="poll icon" />
          <span className="text">Poll</span>
        </MenuItem>
        {[YoutubeEmbedConfig, TwitterEmbedConfig].map((config) => (
          <MenuItem
            _hover={{ bgColor: 'anti-flash-white' }}
            rounded={'0.5rem'}
            py={'8px'}
            px={'10px'}
            _focus={{ bgColor: 'anti-flash-white' }}
            className="menu-item"
            key={config.type}
            onClick={() => handleEmbed(config)}
          >
            {config.icon}
            <span>{config.contentName}</span>
          </MenuItem>
        ))}
      </MenuList>
      {modal}
    </Menu>
  );
};
